/**
 * Permission codes the Enterprise Setup screens declare an interest in.
 *
 * They are the dot-strings `internal/foundation/permissions.go` registers with the
 * RBAC catalogue (CLAUDE.md §6) and that arrive on `/auth/me`. They live beside the
 * feature rather than in `@/lib/permissions` for the same reason
 * `modules/org/permissions.ts` does: the shared file belongs to another workstream,
 * and folding them in later is a one-line move.
 *
 * FSD 1.2.1.10 puts the whole feature behind the Enterprise Administrator role
 * holding `foundation.enterprise.manage`, and then splits one authority off it:
 * **activate and reject are the approver's, never the administrator's.** That split
 * is not cosmetic — FND-F01-V07/AC05 say "a submitted configuration shall not be
 * approved by its creator", and the server enforces it with `ERR_SOD_VIOLATION`
 * against the recorded submitter. The screens mirror it by gating Apply and Reject
 * on `enterpriseActivate` alone, so an administrator who submitted the pass is never
 * shown a button that exists only to refuse them.
 *
 * Defaults, flags and packs each carry their own manage code rather than riding on
 * `manage`, because FSD 1.2.1.10 requires a *different* approval posture for each: a
 * flag that reduces protection needs a named second approver, a default change needs
 * an impact review. A single code could not express that.
 */

export const ENTERPRISE_PERMISSIONS = {
  /** Read the enterprise, its defaults, flags, packs and version history. */
  enterpriseRead: 'foundation.enterprise.read',

  /**
   * Capture identity, run guided configuration (propose / validate / submit).
   * Deliberately NOT sufficient for Apply — see the note above.
   */
  enterpriseManage: 'foundation.enterprise.manage',

  /**
   * Approver-only: activate the enterprise, apply the guided configuration, and
   * reject either back to draft. The one irreversible authority in this feature.
   */
  enterpriseActivate: 'foundation.enterprise.activate',

  /** Set a global default (F-2.002); every change is audited with its prior value. */
  defaultsManage: 'foundation.enterprise.defaults.manage',

  /** Toggle a platform-wide flag (F-2.008): PII encryption, AI assistance, telemetry. */
  flagManage: 'foundation.enterprise.flag.manage',

  /** Activate or deactivate an industry pack (F-2.004 / FND-F01-V09). */
  packManage: 'foundation.enterprise.pack.manage',
} as const

export type EnterprisePermission =
  (typeof ENTERPRISE_PERMISSIONS)[keyof typeof ENTERPRISE_PERMISSIONS]
